import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProjects } from "../services/api";
import { projects as mockProjects } from "../mock";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Button } from "../components/ui/button";
import { LayoutGrid } from "lucide-react";

const placeholder = "https://images.unsplash.com/photo-1636990729669-70b389c7662a?auto=format&fit=crop&w=1400&q=60";

function slugify(text) {
  return (text || "").toString().toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-");
}

export default function ProjectDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        // try to fetch the project list and find the item (works for both UUID and slug)
        const list = await getProjects().catch(() => []);
        let found = list.find((p) => p.id === id) || list.find((p) => slugify(p.title) === id);
        if (!found) {
          // fallback to mock data
          found = mockProjects.find((p) => p.id === id) || mockProjects.find((p) => slugify(p.title) === id);
        }
        if (mounted) setItem(found || null);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id]);

  const sections = useMemo(() => ([
    { label: "Problem", key: "problem" },
    { label: "Approach", key: "approach" },
    { label: "Tools/Standards", key: "tools" },
    { label: "Outcome", key: "outcome" },
    { label: "Key Learnings", key: "learnings" },
  ]), []);

  if (loading) {
    return <div className="mx-auto max-w-5xl px-4 py-16">Loading case study…</div>;
  }

  if (!item) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-16">
        <h1 className="text-2xl font-bold">Case study not found</h1>
        <p className="text-slate-600 mt-2">The requested project could not be located.</p>
        <Link to="/" className="inline-block mt-4">
          <Button variant="outline">Back to Home</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-10">
      <nav className="text-sm text-slate-500 mb-4">
        <Link to="/" className="hover:text-slate-900">Home</Link>
        <span className="mx-2">/</span>
        <a href="#portfolio" className="hover:text-slate-900">Projects</a>
        <span className="mx-2">/</span>
        <span className="text-slate-900">{item.title}</span>
      </nav>

      <div className="rounded-2xl overflow-hidden border bg-white">
        <div className="relative h-56 w-full">
          <img src={item.thumbnail || placeholder} alt={item.title} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
            <div>
              <h1 className="text-white text-2xl md:text-3xl font-extrabold drop-shadow">{item.title}</h1>
              <div className="mt-2">{item.category && <Badge className="bg-sky-600 text-white">{item.category}</Badge>}</div>
            </div>
            <Link to="#contact">
              <Button className="bg-sky-600 text-white">Request Consulting</Button>
            </Link>
          </div>
        </div>

        <div className="p-6">
          <div className="text-slate-600 mb-6 flex items-center gap-2"><LayoutGrid className="h-4 w-4 text-sky-600" /> Structured Case Study</div>
          <Separator />
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {sections.map((s) => (
              item[s.key] ? (
                <Card key={s.key}>
                  <CardHeader className="pb-2"><CardTitle className="text-slate-900">{s.label}</CardTitle></CardHeader>
                  <CardContent className="text-sm text-slate-700 whitespace-pre-wrap">{item[s.key]}</CardContent>
                </Card>
              ) : null
            ))}
          </div>
          <div className="mt-8 flex gap-3">
            <Link to="/#portfolio"><Button variant="outline">Back to Projects</Button></Link>
            <a href="#contact"><Button className="bg-sky-600 text-white">Discuss This</Button></a>
          </div>
        </div>
      </div>
    </div>
  );
}